/**
 * SortDropdown - Sorting options for restaurant list
 */
import { memo, useState, useCallback, useEffect } from 'react';
import { ChevronDown, Check, ArrowUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFilterStore } from '@/lib/FilterStore';
import { useShallow } from 'zustand/react/shallow';

interface SortOption {
  value: string;
  label: string;
  description?: string;
}

const SORT_OPTIONS: SortOption[] = [
  { value: 'Empfohlen', label: 'Empfohlen', description: 'Beste für dich' },
  { value: 'distance', label: 'Entfernung', description: 'Nächste zuerst' },
  { value: 'rating', label: 'Bewertung', description: 'Beste zuerst' },
  { value: 'popular', label: 'Beliebtheit', description: 'Meistbesucht' },
  { value: 'newest', label: 'Neueste', description: 'Zuletzt hinzugefügt' },
];

interface SortDropdownProps {
  className?: string;
  hasLocation?: boolean;
}

export const SortDropdown = memo(function SortDropdown({ 
  className,
  hasLocation = false
}: SortDropdownProps) {
  const [open, setOpen] = useState(false);
  
  const { sortOption, setSortOption } = useFilterStore(useShallow(state => ({
    sortOption: state.sortOption,
    setSortOption: state.setSortOption
  })));
  
  const currentOption = SORT_OPTIONS.find(o => o.value === sortOption) || SORT_OPTIONS[0];
  
  const handleSelect = useCallback((value: string) => {
    setSortOption(value);
    setOpen(false);
  }, [setSortOption]);
  
  // Close on Escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false);
  }, []);
  
  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);
  
  // Filter options based on location availability
  const availableOptions = SORT_OPTIONS.filter(option => {
    if (option.value === 'distance' && !hasLocation) return false;
    return true;
  });
  
  return (
    <div className={cn("relative", className)}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-2 px-4 rounded-xl border transition-colors h-full",
          "text-sm font-medium",
          open 
            ? "border-[#009544] bg-[#E8F5EE] text-[#009544]" 
            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
        )}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <ArrowUpDown className="w-4 h-4" />
        <span>{currentOption.label}</span>
        <ChevronDown className={cn(
          "w-4 h-4 transition-transform",
          open && "rotate-180"
        )} />
      </button>
      
      {open && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-[100]" 
            onClick={() => setOpen(false)}
          />
          
          {/* Dropdown - rechts ausgerichtet mit max-width */}
          <div 
            className="absolute top-full right-0 mt-2 z-[110] w-[200px] max-w-[calc(100vw-32px)] bg-white rounded-xl shadow-lg border border-gray-200 py-1 overflow-hidden"
            style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
            role="listbox"
          >
            {availableOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={cn(
                  "w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors",
                  sortOption === option.value 
                    ? "bg-[#E8F5EE]" 
                    : "hover:bg-gray-50"
                )}
                role="option"
                aria-selected={sortOption === option.value}
              >
                <div className="flex flex-col">
                  <span className={cn(
                    "text-sm font-medium",
                    sortOption === option.value ? "text-[#009544]" : "text-gray-900"
                  )}>
                    {option.label}
                  </span>
                  {option.description && (
                    <span className="text-xs text-gray-500">{option.description}</span>
                  )}
                </div>
                {sortOption === option.value && (
                  <Check className="w-4 h-4 text-[#009544]" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
});
